import { useRef, useState, useEffect } from "react";
import { useEditProfileModalStyles } from "./styles";
import { CustomSelectInput } from "../ProfileInfo/components/SelectInput";
import IMAGE from "../../../../assets/images/user-image.png";
import useFetch from "../../../../hooks/useFetch";
import { getUserId } from "../../../../utils/auth";
import { toast } from "react-toastify";
import CloseIcon from "@material-ui/icons/Close";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

export const EditProfileModal = ({
  isOpen,
  onClose,
  profileData = {},
  setProfileData,
  langState = { selected: [], added: [], deleted: [] },
  allLanguages = [],
  toggleLanguage,
  handleOnProfileInfoSave,
  updateInfoLoading,
}) => {
  const classes = useEditProfileModalStyles();
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const userId = getUserId();

  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [pendingPhotoPreview, setPendingPhotoPreview] = useState(null);
  const [pendingVideoFile, setPendingVideoFile] = useState(null);
  const [pendingVideoPreview, setPendingVideoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [phoneCountry, setPhoneCountry] = useState("in");

  const { mutate: uploadPhoto, loading: uploadPhotoLoading } = useFetch(
    "addOrUpdateMentorProfileImage",
    { method: "post", manual: true },
  );

  const { mutate: uploadVideo, loading: uploadVideoLoading } = useFetch(
    "/mentor/profileVideo",
    { method: "post", manual: true },
  );

  const {
    FNAME: firstName = "",
    LNAME: lastName = "",
    email = "",
    contact_no = "",
    totalYears = "",
    about = "",
    degree = "",
    college = "",
    photo,
    videoUrl,
    mockDemoVideoUrl = "",
  } = profileData;

  const validateFields = () => {
    const newErrors = {};
    if (!firstName?.trim()) newErrors.FNAME = "First name is required";
    const contactStr = contact_no?.toString().trim();
    if (!contactStr) {
      newErrors.contact_no = "Contact number is required";
    } else {
      const parsed = parsePhoneNumberFromString("+" + contactStr, phoneCountry.toUpperCase());
      if (!parsed || !parsed.isValid()) {
        newErrors.contact_no = "Enter a valid mobile number for the selected country";
      }
    }
    if (!totalYears?.toString().trim()) newErrors.totalYears = "Total teaching experience is required";
    if (!langState.selected?.length) newErrors.languages = "At least one language is required";
    if (!degree?.trim()) newErrors.degree = "Degree is required";
    if (!college?.trim()) newErrors.college = "College is required";
    if (!about?.trim()) newErrors.about = "About me is required";
    return newErrors;
  };

  const handleChange = (e) => {
    setProfileData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPendingPhotoFile(file);
    setPendingPhotoPreview(URL.createObjectURL(file));
    e.target.value = null;
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPendingVideoFile(file);
    setPendingVideoPreview(URL.createObjectURL(file));
    e.target.value = null;
  };

  const handleSave = async () => {
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    if (pendingPhotoFile) {
      const formData = new FormData();
      formData.append("profile", pendingPhotoFile);
      await uploadPhoto({
        data: formData,
        onSuccess: (data) => {
          setProfileData((prev) => ({ ...prev, photo: data?.photo }));
          toast.success("Profile picture updated");
        },
        onError: () => toast.error("Profile picture upload failed"),
      });
    }
    if (pendingVideoFile) {
      const formData = new FormData();
      formData.append("file", pendingVideoFile);
      formData.append("mentorId", userId);
      await uploadVideo({
        data: formData,
        onSuccess: (data) => {
          setProfileData((prev) => ({ ...prev, videoUrl: data?.videoUrl }));
          toast.success("Intro video updated");
        },
        onError: () => toast.error("Video upload failed"),
      });
    }
    await handleOnProfileInfoSave();
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={classes.overlay} onClick={onClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        <div className={classes.header}>
          <span className={classes.title}>Edit profile</span>
          <button className={classes.closeBtn} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className={classes.scrollBody}>
          <div className={classes.mediaRow}>
            <input
              ref={photoInputRef}
              type="file"
              accept=".jpg,.png,.jpeg"
              className={classes.hiddenInput}
              onChange={handlePhotoSelect}
            />
            <div className={classes.mediaItem}>
              {pendingPhotoPreview || photo ? (
                <div className={classes.profilePicBorder}>
                  <img
                    src={pendingPhotoPreview || photo}
                    alt="profile"
                    className={classes.profilePic}
                  />
                </div>
              ) : (
                <div className={classes.profilePicPlaceholder}>
                  <img
                    src={IMAGE}
                    alt="profile"
                    className={classes.profilePicPlaceholderImg}
                  />
                </div>
              )}
              <div
                className={classes.editOverlay}
                onClick={() => photoInputRef.current.click()}
              >
                <PencilIcon />
              </div>
            </div>

            <input
              ref={videoInputRef}
              type="file"
              accept=".mp4"
              className={classes.hiddenInput}
              onChange={handleVideoSelect}
            />
            <div className={classes.mediaItem}>
              {pendingVideoPreview || videoUrl ? (
                <video key={pendingVideoPreview || videoUrl} className={classes.videoThumb}>
                  <source
                    src={pendingVideoPreview || videoUrl}
                    type="video/mp4"
                  />
                </video>
              ) : (
                <div
                  className={classes.videoPlaceholder}
                  onClick={() => videoInputRef.current.click()}
                >
                  Upload intro video
                </div>
              )}
              {(pendingVideoPreview || videoUrl) && (
                <div
                  className={classes.editOverlay}
                  onClick={() => videoInputRef.current.click()}
                >
                  <PencilIcon />
                </div>
              )}
            </div>
          </div>

          <div className={classes.fieldGroup}>
            <label className={classes.label}>
              First name <span>*</span>
            </label>
            <input
              className={`${classes.input} ${errors.FNAME ? classes.inputError : ""}`}
              name="FNAME"
              value={firstName}
              onChange={handleChange}
              placeholder="First name"
            />
            {errors.FNAME && <span className={classes.errorText}>{errors.FNAME}</span>}
          </div>

          <div className={classes.fieldGroup}>
            <label className={classes.label}>Last name</label>
            <input
              className={classes.input}
              name="LNAME"
              value={lastName}
              onChange={handleChange}
              placeholder="Last name"
            />
          </div>

          <div className={classes.fieldGroup}>
            <label className={classes.label}>
              Contact number <span>*</span>
            </label>
            <div className={`${classes.phoneInputWrapper} ${errors.contact_no ? classes.phoneInputError : ""}`}>
              <PhoneInput
                country={phoneCountry}
                value={contact_no?.toString()}
                onChange={(phone, countryData) => {
                  setPhoneCountry(countryData.countryCode);
                  setProfileData((prev) => ({ ...prev, contact_no: phone }));
                  if (errors.contact_no) {
                    setErrors((prev) => ({ ...prev, contact_no: undefined }));
                  }
                }}
                enableSearch
                inputStyle={{ width: "100%" }}
              />
            </div>
            {errors.contact_no && <span className={classes.errorText}>{errors.contact_no}</span>}
          </div>

          <div className={classes.fieldGroup}>
            <label className={classes.label}>Email</label>
            <input
              className={classes.input}
              name="email"
              value={email}
              disabled
              placeholder="Email"
            />
          </div>

          <div className={classes.fieldGroup}>
            <label className={classes.label}>
              Total teaching experience <span>*</span>
            </label>
            <input
              className={`${classes.input} ${errors.totalYears ? classes.inputError : ""}`}
              name="totalYears"
              value={totalYears}
              onChange={handleChange}
              placeholder="Years of experience"
              type="number"
            />
            {errors.totalYears && <span className={classes.errorText}>{errors.totalYears}</span>}
          </div>

          <div className={classes.fieldGroup}>
            <label className={classes.label}>
              Languages <span>*</span>
            </label>
            <div className={`${classes.selectWrapper} ${errors.languages ? classes.selectWrapperError : ""}`}>
              <CustomSelectInput
                value={langState.selected}
                options={allLanguages}
                onChange={(val) => {
                  toggleLanguage(val);
                  setErrors((prev) => ({ ...prev, languages: undefined }));
                }}
              />
            </div>
            {errors.languages && <span className={classes.errorText}>{errors.languages}</span>}
          </div>

          <div className={classes.fieldGroup}>
            <label className={classes.label}>
              Degree <span>*</span>
            </label>
            <input
              className={`${classes.input} ${errors.degree ? classes.inputError : ""}`}
              name="degree"
              value={degree}
              onChange={handleChange}
              placeholder="e.g. B.Tech"
            />
            {errors.degree && <span className={classes.errorText}>{errors.degree}</span>}
          </div>

          <div className={classes.fieldGroup}>
            <label className={classes.label}>
              College <span>*</span>
            </label>
            <input
              className={`${classes.input} ${errors.college ? classes.inputError : ""}`}
              name="college"
              value={college}
              onChange={handleChange}
              placeholder="e.g. IIT Hyderabad"
            />
            {errors.college && <span className={classes.errorText}>{errors.college}</span>}
          </div>

          <div className={classes.fieldGroup}>
            <label className={classes.label}>Mock demo video link</label>
            <input
              className={classes.input}
              name="mockDemoVideoUrl"
              value={mockDemoVideoUrl}
              onChange={handleChange}
              placeholder="Enter YouTube or Drive link"
            />
          </div>
          <div className={classes.fieldGroup}>
            <label className={classes.label}>
              About me <span>*</span>
            </label>
            <textarea
              className={`${classes.textarea} ${errors.about ? classes.textareaError : ""}`}
              name="about"
              value={about}
              onChange={handleChange}
              placeholder="Tell us about yourself"
              rows={4}
            />
            {errors.about && <span className={classes.errorText}>{errors.about}</span>}
          </div>
        </div>

        <div className={classes.footer}>
          <button
            className={classes.saveBtn}
            onClick={handleSave}
            disabled={
              updateInfoLoading || uploadPhotoLoading || uploadVideoLoading
            }
          >
            {(updateInfoLoading ||
              uploadPhotoLoading ||
              uploadVideoLoading) && <span className={classes.spinner} />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
};
